import { getScheduleKey, escapeHtml, getVisibleLines } from "./utils.js";
import { getStopName } from "./line-config.js";
import { patchDOM } from "./dom-utils.js";
import { CITY_STATIONS } from "./trains-tab.js";
import {
  getNotificationConfig,
  saveNotificationConfig,
  renderNotificationSettings,
  bindNotificationEvents,
  sanitizeNotifications
} from "./notifications.js";
import { startOnboarding } from "./onboarding.js";
import { TRAIN_STATIONS } from "../data/trains.js";
import { sanitizeSettings, clampNumber, buildDefaultSettings } from "./settings-logic";
import { renderCloudSyncSection, bindCloudSyncEvents } from "./cloud-sync-ui.js";
import { safeRenderLineSettings } from "./line-settings.js";

// Re-export for backward compatibility (main.js, store.js import sanitizeSettings from here)
export { sanitizeSettings };

let lastArgs = null;

export function renderSettings(state, saveFn, cfg, lineData, lineConfig) {
  lastArgs = { state, saveFn, cfg, lineData, lineConfig };
  // Register the settings event-bus listener exactly once per page lifetime
  // (B14): notification UI controls dispatch `trasporti:settings-changed`
  // and we re-render in place when the Settings tab is active. This
  // replaces the brittle `window._app_config.renderSettings` indirection.
  if (!window.__settingsBusBound) {
    document.addEventListener("trasporti:settings-changed", () => {
      if (lastArgs && lastArgs.state && lastArgs.state.currentTab === "settings") {
        const { state: s, saveFn: sf, cfg: c, lineData: ld, lineConfig: lc } = lastArgs;
        renderSettings(s, sf, c, ld, lc);
      }
    });

    document.addEventListener("trasporti:sync-locked", () => {
      // Cloud sync is encrypted, but passphrase is not set. Reload to show warning.
      if (lastArgs && lastArgs.state && lastArgs.state.currentTab === "settings") {
        const { state: s, saveFn: sf, cfg: c, lineData: ld, lineConfig: lc } = lastArgs;
        renderSettings(s, sf, c, ld, lc);
      }
    });

    document.addEventListener("trasporti:sync-error-passphrase", () => {
      alert(
        "⚠️ Errore di decrittografia del cloud. La tua Passphrase di Sincronizzazione locale non è corretta."
      );
    });

    window.__settingsBusBound = true;
  }

  const container = document.getElementById("settings-content");
  if (!container) return;
  const settings = state.settings || {};
  const visibleLines = getVisibleLines(state, cfg);
  const activeLine = state.settingsPanelLine || visibleLines[0] || "Z649";

  const lineOptions = visibleLines
    .map(
      (lineId) =>
        `<button type="button" data-settings-line="${lineId}" class="${lineId === activeLine ? "active" : ""}">${lineId}</button>`
    )
    .join("");

  const selectedFocusCity = settings.focusCity || cfg.defaults.focusCity || "BT";
  const focusCityOptions = Object.entries(cfg.focusCities || {})
    .map(
      ([code, city]) =>
        `<option value="${code}" ${code === selectedFocusCity ? "selected" : ""}>${escapeHtml(city.name)}</option>`
    )
    .join("");

  const ALL_STATIONS = Object.keys(TRAIN_STATIONS);
  const relevantStations = CITY_STATIONS[selectedFocusCity] || ALL_STATIONS;
  const stationsToRender = state.showAllStations ? ALL_STATIONS : relevantStations;

  const stationsHtml = stationsToRender
    .map((station) => {
      const info = TRAIN_STATIONS[station];
      if (!info) return "";
      const value =
        settings.stationReachMinutes?.[station] !== undefined
          ? settings.stationReachMinutes[station]
          : 0;
      const labelText = station === "CN_FS"
        ? `Minuti in auto per raggiungere ${escapeHtml(info.name)}`
        : `Minuti per raggiungere ${escapeHtml(info.name)}`;
      return `
      <label class="field-row">
        <span>${labelText}</span>
        <input type="number" min="0" max="60" data-station-reach-setting="${station}" value="${Number(value)}">
      </label>
    `;
    })
    .join("");

  const showAllStationsBtn = !state.trainsShowAllStations
    ? `
    <div class="settings-stations-toggle-wrapper">
      <button type="button" class="btn btn-secondary text-btn settings-stations-toggle-btn" data-action-show-all-stations>
        🌐 Mostra tutte le stazioni ferroviarie
      </button>
    </div>
  `
    : `
    <div class="settings-stations-toggle-wrapper">
      <button type="button" class="btn btn-secondary text-btn settings-stations-toggle-btn" data-action-hide-all-stations>
        👁️ Mostra solo stazioni pertinenti
      </button>
    </div>
  `;

  const settingsHeroCode = settings.liveHero || "Z649";
  const settingsVisibleTrains = settings.visibleTrains || ["CN_FS"];

  const settingsHeroOptions = [
    ...visibleLines.map(
      (id) =>
        `<option value="${id}" ${settingsHeroCode === id ? "selected" : ""}>Bus ${id} - ${escapeHtml(lineConfig[id]?.destination || id)}</option>`
    ),
    ...Object.entries(TRAIN_STATIONS).map(
      ([code, info]) =>
        `<option value="${code}" ${settingsHeroCode === code ? "selected" : ""}>Treno - Stazione di ${escapeHtml(info.name)}</option>`
    )
  ].join("");

  const settingsTrainsCheckboxes = Object.entries(TRAIN_STATIONS)
    .map(([code, info]) => {
      const isChecked = settingsVisibleTrains.includes(code);
      return `
      <label class="settings-check-card">
        <input type="checkbox" data-settings-visible-train="${code}" ${isChecked ? "checked" : ""}>
        <span>${escapeHtml(info.name)}</span>
      </label>
    `;
    })
    .join("");

  const activeSubTab = state.settingsSubTab || "profile";

  const settingsFooterLinks = `
    <div class="settings-footer-links">
      <a href="./privacy.html" target="_blank" rel="noopener" class="settings-footer-link">📋 Privacy & GDPR</a>
      <span class="settings-footer-separator">|</span>
      <a href="./feedback.html" target="_blank" rel="noopener" class="settings-footer-link">🐛 Segnala un problema</a>
      <span class="settings-footer-separator">|</span>
      <a href="./feedback.html" target="_blank" rel="noopener" class="settings-footer-link">💡 Suggerisci miglioramento</a>
    </div>
  `;

  // Linee bus visibili: checkbox per la selezione (stessa logica del modal LIVE)
  const focusCity = settings.focusCity || cfg.defaults?.focusCity || "BT";
  const cityName = cfg.focusCities?.[focusCity]?.name || focusCity;
  const cityLineOrder = cfg.focusCities?.[focusCity]?.lineOrder || cfg.lineOrder || [];
  const allBusLines = Object.keys(lineConfig).sort();
  const followedForCity = settings.followedLinesByCity?.[focusCity];

  const isLineChecked = (lineId) => {
    if (Array.isArray(followedForCity)) return followedForCity.includes(lineId);
    return cityLineOrder.includes(lineId);
  };

  const cityBusCheckboxes = cityLineOrder
    .map(
      (id) => `
    <label class="settings-check-card">
      <input type="checkbox" data-settings-visible-line="${id}" ${isLineChecked(id) ? "checked" : ""}>
      <span style="font-weight: 600;">${id}</span>
    </label>
  `
    )
    .join("");

  const otherBusLines = allBusLines.filter((id) => !cityLineOrder.includes(id));
  const otherBusCheckboxes = otherBusLines
    .map(
      (id) => `
    <label class="settings-check-card">
      <input type="checkbox" data-settings-visible-line="${id}" ${isLineChecked(id) ? "checked" : ""}>
      <span>${id}</span>
    </label>
  `
    )
    .join("");

  const subtabsHtml = `
    <div class="settings-subtabs" role="navigation" aria-label="Sotto-sezioni impostazioni">
      <button type="button" class="subtab-btn ${activeSubTab === "profile" ? "active" : ""}" data-settings-subtab="profile">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
        <span>Profilo</span>
      </button>
      <button type="button" class="subtab-btn ${activeSubTab === "display" ? "active" : ""}" data-settings-subtab="display">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="9" y1="21" x2="9" y2="9"></line></svg>
        <span>Interfaccia</span>
      </button>
      <button type="button" class="subtab-btn ${activeSubTab === "lines" ? "active" : ""}" data-settings-subtab="lines">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M12 22s-8-4.5-8-11.8A8 8 0 0 1 20 10.2c0 7.3-8 11.8-8 11.8z"></path><circle cx="12" cy="10" r="3"></circle></svg>
        <span>Fermate</span>
      </button>
      <button type="button" class="subtab-btn ${activeSubTab === "notifications" ? "active" : ""}" data-settings-subtab="notifications">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path></svg>
        <span>Notifiche</span>
      </button>
      <button type="button" class="subtab-btn ${activeSubTab === "system" ? "active" : ""}" data-settings-subtab="system">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
        <span>Sincronizzazione</span>
      </button>
    </div>
  `;

  const html = `
    ${subtabsHtml}

    <div class="settings-section-wrapper ${activeSubTab === "profile" ? "" : "hidden"}" data-card="settings-profile-section">
      <section class="panel">
        <div class="panel-heading">
          <div>
            <p class="section-eyebrow">Profilo casa</p>
            <h2>${escapeHtml(cfg.homeProfile.address)}</h2>
            <p>${escapeHtml(cfg.homeProfile.note || "Configurazione attiva concentrata sulla tua città.")}</p>
          </div>
        </div>
        <div class="settings-grid">
          <label class="field-row">
            <span>Città di Focus</span>
            <select data-setting-string="focusCity">
              ${focusCityOptions}
            </select>
          </label>
          <label class="field-row">
            <span>Minuti a piedi verso fermata principale</span>
            <input type="number" min="1" max="30" data-setting-number="walkRossini" value="${Number(settings.walkRossini || cfg.defaults.walkRossini)}">
          </label>
          <label class="settings-invert-card">
            <input type="checkbox" id="settings-invert-directions" ${settings.invertDirections ? "checked" : ""}>
            <span>Inverti Andata/Ritorno (utile se abiti a Milano)</span>
          </label>
          <label class="settings-invert-card" style="margin-top: 6px;">
            <input type="checkbox" id="settings-large-text" ${settings.largeText ? "checked" : ""}>
            <span>Testo Maggiorato (Accessibilità 🔍)</span>
          </label>
        </div>
          <div class="settings-walk-drive-preview settings-walk-drive-preview-card">
            <div>🏃 <strong>Impatto Cammino (Busto G. Rossini):</strong> Con <span id="walk-val-preview">${Number(settings.walkRossini || cfg.defaults.walkRossini)}</span> min di cammino, se il bus parte tra 15 min, avrai un margine di <strong>partenza di <span id="walk-countdown-preview">${15 - Number(settings.walkRossini || cfg.defaults.walkRossini)}</span> min</strong>.</div>
            <div>🚗 <strong>Impatto Auto (Canegrate FS):</strong> Con <span id="drive-val-preview">${Number(settings.stationReachMinutes?.CN_FS !== undefined ? settings.stationReachMinutes.CN_FS : (settings.driveCanegrate || 16))}</span> min di auto, se il treno parte tra 20 min, avrai un margine di <strong>partenza di <span id="drive-countdown-preview">${20 - Number(settings.stationReachMinutes?.CN_FS !== undefined ? settings.stationReachMinutes.CN_FS : (settings.driveCanegrate || 16))}</span> min</strong>.</div>
          </div>
        </div>
        <div class="settings-section-divider">
          <h3 class="settings-section-subtitle">Tempi per raggiungere la stazione (minuti)</h3>
          <div class="settings-grid stations-reach">
            ${stationsHtml}
          </div>
          ${showAllStationsBtn}
        </div>
      </section>
      ${settingsFooterLinks}
    </div>

    <div class="settings-section-wrapper ${activeSubTab === "display" ? "" : "hidden"}" data-card="settings-display-section">
      <section class="panel">
        <div class="panel-heading">
          <div>
            <p class="section-eyebrow">Personalizzazione LIVE e ORARI</p>
            <h2>Layout e Contenuto</h2>
            <p>Configura la scheda principale (Hero) e seleziona quali stazioni/linee visualizzare.</p>
          </div>
        </div>
        <div class="settings-grid">
          <label class="field-row">
            <span>Scheda principale (HERO)</span>
            <select data-settings-live-hero>
              ${settingsHeroOptions}
            </select>
          </label>
          <label class="field-row">
            <span>Massimo cambi (Pianificatore)</span>
            <select data-setting-number="maxTransfers">
              <option value="1" ${Number(settings.maxTransfers || 1) === 1 ? "selected" : ""}>1 cambio (consigliato)</option>
              <option value="2" ${Number(settings.maxTransfers || 1) === 2 ? "selected" : ""}>2 cambi (percorsi complessi)</option>
            </select>
          </label>
        </div>
        <div class="settings-section-divider">
          <h3 style="font-size: 0.95rem; font-weight: 600; color: var(--foreground); margin-bottom: 0.5rem;">Soglie di Coincidenza (Minuti di attesa)</h3>
          <p style="font-size: 0.75rem; color: var(--muted); margin: 0 0 1rem 0;">Imposta i tempi limite di attesa per la classificazione visiva delle coincidenze.</p>
          
          <div id="settings-threshold-warning" class="warning-banner inline-warning settings-threshold-warning-banner ${!(Number(settings.connectionTightMin || 4) < Number(settings.connectionGoodMin || 12) && Number(settings.connectionGoodMin || 12) < Number(settings.connectionLongMin || 25)) ? "" : "hidden"}">
            <span>⚠️ Le soglie devono essere in ordine crescente: Stretta &lt; Buona &lt; Comoda. I valori non validi verranno ripristinati ai default (4, 12, 25).</span>
          </div>

          <div class="settings-sliders-grid settings-sliders-container">
            <div class="settings-slider-group">
              <label class="field-row-slider settings-slider-label-row">
                <span class="slider-label settings-slider-title">Coincidenza Stretta (Rosso 🔴)</span>
                <span class="slider-value-badge settings-slider-badge" id="val-connectionTightMin">${Number(settings.connectionTightMin || cfg.defaults.connectionTightMin || 4)} min</span>
              </label>
              <input type="range" min="1" max="20" data-setting-slider="connectionTightMin" value="${Number(settings.connectionTightMin || cfg.defaults.connectionTightMin || 4)}" class="settings-slider-input">
              <p class="slider-preview-text settings-slider-desc" id="preview-connectionTightMin">Coincidenze con attesa &lt; ${Number(settings.connectionTightMin || 4)} min saranno classificate come "stretta".</p>
            </div>

            <div class="settings-slider-group">
              <label class="field-row-slider settings-slider-label-row">
                <span class="slider-label settings-slider-title">Coincidenza Buona (Giallo/Arancione 🟡)</span>
                <span class="slider-value-badge settings-slider-badge" id="val-connectionGoodMin">${Number(settings.connectionGoodMin || cfg.defaults.connectionGoodMin || 12)} min</span>
              </label>
              <input type="range" min="5" max="45" data-setting-slider="connectionGoodMin" value="${Number(settings.connectionGoodMin || cfg.defaults.connectionGoodMin || 12)}" class="settings-slider-input">
              <p class="slider-preview-text settings-slider-desc" id="preview-connectionGoodMin">Coincidenze tra ${Number(settings.connectionTightMin || 4)} e ${Number(settings.connectionGoodMin || 12)} min saranno classificate come "buona".</p>
            </div>

            <div class="settings-slider-group">
              <label class="field-row-slider settings-slider-label-row">
                <span class="slider-label settings-slider-title">Coincidenza Comoda (Verde 🟢)</span>
                <span class="slider-value-badge settings-slider-badge" id="val-connectionLongMin">${Number(settings.connectionLongMin || cfg.defaults.connectionLongMin || 25)} min</span>
              </label>
              <input type="range" min="10" max="90" data-setting-slider="connectionLongMin" value="${Number(settings.connectionLongMin || cfg.defaults.connectionLongMin || 25)}" class="settings-slider-input">
              <p class="slider-preview-text settings-slider-desc" id="preview-connectionLongMin">Coincidenze tra ${Number(settings.connectionGoodMin || 12) + 1} e ${Number(settings.connectionLongMin || 25)} min saranno classificate come "comoda". Oltre i ${Number(settings.connectionLongMin || 25)} min saranno "lunga".</p>
            </div>
          </div>
        </div>
        <div class="settings-section-divider">
          <h3 class="settings-section-subtitle settings-section-subtitle--small">Linee bus visibili (LIVE e ORARI)</h3>
          <p class="settings-section-description">Scegli quali linee mostrare nei tab LIVE, ORARI, Fermate e Notifiche.</p>
          <div class="settings-lines-group-title">Suggerite per ${escapeHtml(cityName)}</div>
          <div class="settings-grid bus-lines-suggested">
            ${cityBusCheckboxes}
          </div>
          ${
            otherBusLines.length > 0
              ? `
          <div class="settings-lines-group-title settings-lines-group-title--muted">Altre linee del network</div>
          <div class="settings-scroll-box">
            <div class="settings-grid bus-lines-other">
              ${otherBusCheckboxes}
            </div>
          </div>`
              : ""
          }
          <div class="settings-reset-wrapper">
            <button type="button" class="settings-reset-btn" data-reset-visible-lines>Reset ai default della città</button>
          </div>
        </div>
        <div style="margin-top: 1.5rem; border-top: 1px solid var(--line); padding-top: 1.5rem;">
          <h3 class="settings-section-subtitle settings-section-subtitle--small">Stazioni ferroviarie visibili nel tab LIVE</h3>
          <p class="settings-section-description">Seleziona quali stazioni mostrare in fondo al tab LIVE per il monitoraggio in tempo reale.</p>
          <div class="settings-grid train-stations">
            ${settingsTrainsCheckboxes}
          </div>
        </div>
      </section>
      ${settingsFooterLinks}
    </div>

    <div class="settings-section-wrapper ${activeSubTab === "lines" ? "" : "hidden"}" data-card="settings-lines-section">
      <section class="panel">
        <div class="panel-heading">
          <div>
            <p class="section-eyebrow">Fermate e linee</p>
            <h2>Personalizzazione intuitiva</h2>
            <p>Scegli fermate preferite e colonne visibili. I default restano in data/config.js.</p>
          </div>
        </div>
        <div class="line-tabs">${lineOptions}</div>
        ${safeRenderLineSettings(activeLine, state, cfg, lineData, lineConfig)}
      </section>
      ${settingsFooterLinks}
    </div>

    <div class="settings-section-wrapper ${activeSubTab === "notifications" ? "" : "hidden"}" data-card="settings-notifications-section">
      ${renderNotificationSettings(cfg, visibleLines)}
      ${settingsFooterLinks}
    </div>

    <div class="settings-section-wrapper ${activeSubTab === "system" ? "" : "hidden"}" data-card="settings-system-section">
      ${renderCloudSyncSection(cfg)}

      <section class="panel">
        <div class="panel-heading">
          <div>
            <p class="section-eyebrow">Dati e PWA</p>
            <h2>Backup e aggiornamento</h2>
            <p>Le preferenze sono salvate solo nel browser.</p>
          </div>
        </div>
        <div class="button-grid">
          <button type="button" class="btn primary" data-export>Export preferenze</button>
          <button type="button" class="btn secondary" data-import-trigger>Import preferenze</button>
          <button type="button" class="btn secondary" data-reset-preferences>Reset preferenze</button>
          <button type="button" class="btn secondary" data-restart-onboarding>Riavvia procedura guidata</button>
          <button type="button" class="btn secondary" data-check-sw>Aggiorna app</button>
        </div>
        <input type="file" accept=".json,application/json" data-import-file hidden>
        <div class="info-list">
          <div><span>Versione</span><strong>${escapeHtml(cfg.version)}</strong></div>
          <div><span>Aggiornamento dati</span><strong>${escapeHtml(cfg.lastUpdate)}</strong></div>
          <div><span>Validità GTFS Bus</span><strong>${escapeHtml(cfg.feedValidity?.from || "?")} → ${escapeHtml(cfg.feedValidity?.to || "?")}</strong></div>
          <div><span>Fonte Bus</span><strong>Agenzia TPL / Movibus (Open Data)</strong></div>
          <div><span>Fonte Treni</span><strong>Trenord GTFS (Open Data)</strong></div>
          <div><span>Service worker</span><strong data-sw-status>verifica...</strong></div>
        </div>
        ${settingsFooterLinks}
      </section>
    </div>
  `;

  patchDOM(container, html, {
    onAfterPatch: () => {
      bindEvents(container);
      updateSWStatus(container);
    }
  });
}

// Line settings rendering moved to line-settings.js

function bindEvents(container) {
  const { state, saveFn, cfg, lineData, lineConfig } = lastArgs;

  container.querySelectorAll("[data-settings-subtab]").forEach((button) => {
    if (button.__has_click) return;
    button.__has_click = true;
    button.addEventListener("click", () => {
      state.settingsSubTab = button.dataset.settingsSubtab;
      renderSettings(state, saveFn, cfg, lineData, lineConfig);
      // Smooth scroll to top of settings tab content
      const tabContent = document.getElementById("settings-content");
      if (tabContent) {
        tabContent.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  });

  container.querySelectorAll("[data-setting-string]").forEach((select) => {
    if (select.__has_change) return;
    select.__has_change = true;
    select.addEventListener("change", () => {
      const value = select.value;
      saveFn({ [select.dataset.settingString]: value });
    });
  });

  // Live previews for walkRossini and driveCanegrate inputs
  const walkInput = container.querySelector('[data-setting-number="walkRossini"]');
  if (walkInput && !walkInput.__has_input) {
    walkInput.__has_input = true;
    walkInput.addEventListener("input", () => {
      const walk = Number(walkInput.value) || 0;
      const walkVal = document.getElementById("walk-val-preview");
      const walkCd = document.getElementById("walk-countdown-preview");
      if (walkVal) walkVal.textContent = String(walk);
      if (walkCd) walkCd.textContent = String(15 - walk);
    });
  }

  const driveInput = container.querySelector('[data-station-reach-setting="CN_FS"]');
  if (driveInput && !driveInput.__has_input_preview) {
    driveInput.__has_input_preview = true;
    driveInput.addEventListener("input", () => {
      const drive = Number(driveInput.value) || 0;
      const driveVal = document.getElementById("drive-val-preview");
      const driveCd = document.getElementById("drive-countdown-preview");
      if (driveVal) driveVal.textContent = String(drive);
      if (driveCd) driveCd.textContent = String(20 - drive);
    });
  }

  container.querySelectorAll("[data-setting-number]").forEach((input) => {
    if (input.__has_change) return;
    input.__has_change = true;
    input.addEventListener("change", () => {
      const min = Number(input.min || 0);
      const max = Number(input.max || 999);
      const value = Math.max(min, Math.min(max, Number(input.value || 0)));
      input.value = value;
      saveFn({ [input.dataset.settingNumber]: value });
    });
  });

  // Slider event handling (input: live text update; change: save settings)
  container.querySelectorAll("[data-setting-slider]").forEach((slider) => {
    const key = slider.dataset.settingSlider;

    if (!slider.__has_input) {
      slider.__has_input = true;
      slider.addEventListener("input", () => {
        // Update the value badge text
        const badge = document.getElementById(`val-${key}`);
        if (badge) badge.textContent = `${slider.value} min`;

        // Calculate live preview ranges
        const tightSlider = container.querySelector('[data-setting-slider="connectionTightMin"]');
        const goodSlider = container.querySelector('[data-setting-slider="connectionGoodMin"]');
        const longSlider = container.querySelector('[data-setting-slider="connectionLongMin"]');

        if (tightSlider && goodSlider && longSlider) {
          const tightVal = Number(tightSlider.value);
          const goodVal = Number(goodSlider.value);
          const longVal = Number(longSlider.value);

          // Update preview texts
          const tightPreview = document.getElementById("preview-connectionTightMin");
          const goodPreview = document.getElementById("preview-connectionGoodMin");
          const longPreview = document.getElementById("preview-connectionLongMin");

          if (tightPreview) {
            tightPreview.textContent = `Coincidenze con attesa < ${tightVal} min saranno classificate come "stretta".`;
          }
          if (goodPreview) {
            goodPreview.textContent = `Coincidenze tra ${tightVal} e ${goodVal} min saranno classificate come "buona".`;
          }
          if (longPreview) {
            longPreview.textContent = `Coincidenze tra ${goodVal + 1} e ${longVal} min saranno classificate come "comoda". Oltre i ${longVal} min saranno "lunga".`;
          }

          // Check relation ordering and toggle warning
          const warning = document.getElementById("settings-threshold-warning");
          if (warning) {
            if (tightVal < goodVal && goodVal < longVal) {
              warning.classList.add("hidden");
            } else {
              warning.classList.remove("hidden");
            }
          }
        }
      });
    }

    if (!slider.__has_change) {
      slider.__has_change = true;
      slider.addEventListener("change", () => {
        const value = Number(slider.value);
        saveFn({ [key]: value });
      });
    }
  });

  // Show/Hide All Stations click handlers
  const showBtn = container.querySelector("[data-action-show-all-stations]");
  if (showBtn && !showBtn.__has_click) {
    showBtn.__has_click = true;
    showBtn.addEventListener("click", () => {
      state.showAllStations = true;
      renderSettings(state, saveFn, cfg, lineData, lineConfig);
    });
  }

  const hideBtn = container.querySelector("[data-action-hide-all-stations]");
  if (hideBtn && !hideBtn.__has_click) {
    hideBtn.__has_click = true;
    hideBtn.addEventListener("click", () => {
      state.showAllStations = false;
      renderSettings(state, saveFn, cfg, lineData, lineConfig);
    });
  }

  const invertCheckbox = container.querySelector("#settings-invert-directions");
  if (invertCheckbox && !invertCheckbox.__has_change) {
    invertCheckbox.__has_change = true;
    invertCheckbox.addEventListener("change", () => {
      saveFn({ invertDirections: invertCheckbox.checked });
    });
  }

  const largeTextCheckbox = container.querySelector("#settings-large-text");
  if (largeTextCheckbox && !largeTextCheckbox.__has_change) {
    largeTextCheckbox.__has_change = true;
    largeTextCheckbox.addEventListener("change", () => {
      saveFn({ largeText: largeTextCheckbox.checked });
      if (largeTextCheckbox.checked) {
        document.body.classList.add("accessibility-large-text");
      } else {
        document.body.classList.remove("accessibility-large-text");
      }
    });
  }

  container.querySelectorAll("[data-station-reach-setting]").forEach((input) => {
    if (input.__has_change) return;
    input.__has_change = true;
    input.addEventListener("change", () => {
      const station = input.dataset.stationReachSetting;
      const min = Number(input.min || 0);
      const max = Number(input.max || 60);
      const value = Math.max(min, Math.min(max, Number(input.value || 0)));
      input.value = value;
      const stationReachMinutes = { ...state.settings.stationReachMinutes };
      stationReachMinutes[station] = value;
      saveFn({ stationReachMinutes });
    });
  });

  container.querySelectorAll("[data-settings-live-hero]").forEach((select) => {
    if (select.__has_change) return;
    select.__has_change = true;
    select.addEventListener("change", () => {
      saveFn({ liveHero: select.value });
    });
  });

  container.querySelectorAll("[data-settings-visible-train]").forEach((checkbox) => {
    if (checkbox.__has_change) return;
    checkbox.__has_change = true;
    checkbox.addEventListener("change", () => {
      const code = checkbox.dataset.settingsVisibleTrain;
      let visibleTrains = [...(state.settings.visibleTrains || ["CN_FS"])];
      if (checkbox.checked) {
        if (!visibleTrains.includes(code)) visibleTrains.push(code);
      } else {
        visibleTrains = visibleTrains.filter((c) => c !== code);
      }
      saveFn({ visibleTrains });
    });
  });

  // Handler: Linee bus visibili (checkbox)
  container.querySelectorAll("[data-settings-visible-line]").forEach((checkbox) => {
    if (checkbox.__has_change) return;
    checkbox.__has_change = true;
    checkbox.addEventListener("change", () => {
      const focusCity = state.settings.focusCity || cfg.defaults?.focusCity || "BT";
      const checkedLines = [
        ...container.querySelectorAll("[data-settings-visible-line]:checked")
      ].map((cb) => cb.dataset.settingsVisibleLine);
      const followedLinesByCity = structuredClone(state.settings.followedLinesByCity || {});
      followedLinesByCity[focusCity] = checkedLines;
      saveFn({ followedLinesByCity });
    });
  });

  // Handler: Reset linee visibili ai default della città
  const resetVisibleLinesBtn = container.querySelector("[data-reset-visible-lines]");
  if (resetVisibleLinesBtn && !resetVisibleLinesBtn.__has_click) {
    resetVisibleLinesBtn.__has_click = true;
    resetVisibleLinesBtn.addEventListener("click", () => {
      const focusCity = state.settings.focusCity || cfg.defaults?.focusCity || "BT";
      const followedLinesByCity = structuredClone(state.settings.followedLinesByCity || {});
      delete followedLinesByCity[focusCity];
      saveFn({ followedLinesByCity });
    });
  }

  container.querySelectorAll("[data-settings-line]").forEach((button) => {
    if (button.__has_click) return;
    button.__has_click = true;
    button.addEventListener("click", () => {
      state.settingsPanelLine = button.dataset.settingsLine;
      renderSettings(state, saveFn, cfg, lineData, lineConfig);
    });
  });

  container.querySelectorAll("[data-settings-day]").forEach((button) => {
    if (button.__has_click) return;
    button.__has_click = true;
    button.addEventListener("click", () => {
      state.settingsLineDay = button.dataset.settingsDay;
      renderSettings(state, saveFn, cfg, lineData, lineConfig);
    });
  });

  container.querySelectorAll("[data-settings-dir]").forEach((button) => {
    if (button.__has_click) return;
    button.__has_click = true;
    button.addEventListener("click", () => {
      state.settingsLineDirection = button.dataset.settingsDir;
      renderSettings(state, saveFn, cfg, lineData, lineConfig);
    });
  });

  container.querySelectorAll("[data-favorite-stop]").forEach((select) => {
    if (select.__has_change) return;
    select.__has_change = true;
    select.addEventListener("change", () => {
      const [lineId, direction] = select.dataset.favoriteStop.split(":");
      const favoriteStops = structuredClone(state.settings.favoriteStops || {});
      favoriteStops[lineId] = { ...(favoriteStops[lineId] || {}) };
      favoriteStops[lineId][direction] = select.value;
      saveFn({ favoriteStops });
    });
  });

  container.querySelectorAll("[data-timetable-stop]").forEach((input) => {
    if (input.__has_change) return;
    input.__has_change = true;
    input.addEventListener("change", () => {
      const [lineId, scheduleKey] = input.dataset.timetableStop.split(":");
      const checked = [
        ...container.querySelectorAll(`[data-timetable-stop^="${lineId}:${scheduleKey}:"]:checked`)
      ].map((el) => el.dataset.timetableStop.split(":")[2]);
      const timetableStops = structuredClone(state.settings.timetableStops || {});
      timetableStops[lineId] = { ...(timetableStops[lineId] || {}) };

      const direction = scheduleKey.endsWith("_return") ? "return" : "outbound";
      for (const dt of ["weekday", "saturday", "sunday"]) {
        const key = getScheduleKey(lineId, dt, direction);
        timetableStops[lineId][key] = checked;
      }

      saveFn({ timetableStops });
    });
  });

  container.querySelectorAll("[data-reset-line-stops]").forEach((button) => {
    if (button.__has_click) return;
    button.__has_click = true;
    button.addEventListener("click", () => {
      const [lineId, scheduleKey] = button.dataset.resetLineStops.split(":");
      const timetableStops = structuredClone(state.settings.timetableStops || {});
      if (timetableStops[lineId]) {
        const direction = scheduleKey.endsWith("_return") ? "return" : "outbound";
        for (const dt of ["weekday", "saturday", "sunday"]) {
          const key = getScheduleKey(lineId, dt, direction);
          delete timetableStops[lineId][key];
        }
      }
      saveFn({ timetableStops });
      renderSettings(state, saveFn, cfg, lineData, lineConfig);
    });
  });

  const exportBtn = container.querySelector("[data-export]");
  if (exportBtn && !exportBtn.__has_click) {
    exportBtn.__has_click = true;
    exportBtn.addEventListener("click", () => exportSettings(state, cfg));
  }

  const importTriggerBtn = container.querySelector("[data-import-trigger]");
  if (importTriggerBtn && !importTriggerBtn.__has_click) {
    importTriggerBtn.__has_click = true;
    importTriggerBtn.addEventListener("click", () =>
      container.querySelector("[data-import-file]")?.click()
    );
  }

  const importFileInput = container.querySelector("[data-import-file]");
  if (importFileInput && !importFileInput.__has_change) {
    importFileInput.__has_change = true;
    importFileInput.addEventListener("change", (event) =>
      importSettings(event.target, saveFn, state, cfg, lineData, lineConfig)
    );
  }

  const resetPrefBtn = container.querySelector("[data-reset-preferences]");
  if (resetPrefBtn && !resetPrefBtn.__has_click) {
    resetPrefBtn.__has_click = true;
    resetPrefBtn.addEventListener("click", () => {
      if (!confirm("Ripristinare tutte le preferenze predefinite?")) return;
      localStorage.removeItem("trasporti_settings");
      state.settings = buildDefaultSettings(cfg);
      saveFn(state.settings, true);
      renderSettings(state, saveFn, cfg, lineData, lineConfig);
    });
  }

  const checkSWBtn = container.querySelector("[data-check-sw]");
  if (checkSWBtn && !checkSWBtn.__has_click) {
    checkSWBtn.__has_click = true;
    checkSWBtn.addEventListener("click", () => {
      navigator.serviceWorker?.ready.then((reg) => reg.update()).finally(() => location.reload());
    });
  }

  const restartOnboardingBtn = container.querySelector("[data-restart-onboarding]");
  if (restartOnboardingBtn && !restartOnboardingBtn.__has_click) {
    restartOnboardingBtn.__has_click = true;
    restartOnboardingBtn.addEventListener("click", () => {
      startOnboarding((profile) => {
        if (profile && !profile.skipped) {
          const updates = { userProfile: profile };
          if (profile.walkMinutes) updates.walkRossini = profile.walkMinutes;
          if (profile.stationReachMinutes) {
            updates.stationReachMinutes = {
              ...state.settings.stationReachMinutes,
              ...profile.stationReachMinutes
            };
          }
          if (profile.driveCanegrate) {
            updates.driveCanegrate = profile.driveCanegrate;
            if (!updates.stationReachMinutes) {
              updates.stationReachMinutes = { ...state.settings.stationReachMinutes };
            }
            if (updates.stationReachMinutes.CN_FS === undefined || updates.stationReachMinutes.CN_FS === 0) {
              updates.stationReachMinutes.CN_FS = profile.driveCanegrate;
            }
          }
          if (profile.focusCity) updates.focusCity = profile.focusCity;
          if (profile.visibleTrains) updates.visibleTrains = profile.visibleTrains;
          if (profile.liveHero) updates.liveHero = profile.liveHero;
          if (profile.favoriteStops && Object.keys(profile.favoriteStops).length > 0) {
            updates.favoriteStops = { ...state.settings.favoriteStops, ...profile.favoriteStops };
          }
          saveFn(updates);
        } else if (profile) {
          saveFn({ userProfile: profile });
        }
        renderSettings(state, saveFn, cfg, lineData, lineConfig);
      });
    });
  }

  // Bind notification events
  bindNotificationEvents(container);

  // Bind cloud-sync events (B2)
  bindCloudSyncEvents(container, () => ({
    state,
    saveFn,
    cfg,
    lineData,
    lineConfig,
    renderSettings
  }));
}

// sanitizeSettings, clampNumber, buildDefaultSettings moved to settings-logic.js

function exportSettings(state, cfg) {
  const data = {
    version: cfg.version,
    exportedAt: new Date().toISOString(),
    settings: state.settings,
    notifications: getNotificationConfig()
  };
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `trasporti-preferenze-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

function importSettings(input, saveFn, state, cfg, lineData, lineConfig) {
  const file = input.files?.[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = (event) => {
    try {
      const parsed = JSON.parse(event.target.result);
      if (!parsed.settings || typeof parsed.settings !== "object")
        throw new Error("preferenze mancanti");
      // Apply notifications config first so the saveFn (which triggers a
      // cloud push including notifications) sees the imported value (B16).
      if (parsed.notifications !== undefined) {
        saveNotificationConfig(sanitizeNotifications(parsed.notifications));
      }
      saveFn(sanitizeSettings(parsed.settings, cfg));
      renderSettings(state, saveFn, cfg, lineData, lineConfig);
      alert("Preferenze importate.");
    } catch (error) {
      alert(`Import non valido: ${error.message}`);
    }
  };
  reader.readAsText(file);
  input.value = "";
}

// Cloud sync rendering and events moved to cloud-sync-ui.js

function updateSWStatus(container) {
  const el = container.querySelector("[data-sw-status]");
  if (!el) return;
  if (!("serviceWorker" in navigator)) {
    el.textContent = "non disponibile";
    return;
  }
  navigator.serviceWorker.ready
    .then(() => {
      el.textContent = "attivo";
    })
    .catch(() => {
      el.textContent = "non installato";
    });
}
